import { DietPlan } from "@/services/api";

// Number of days after a diet plan's last edit before the client is flagged
// for review on the Clients list.
export const DIET_REVIEW_THRESHOLD_DAYS = 10;

const toTime = (value?: string | Date | null): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Most recent edit across a set of diet plans (prefers updatedAt, falls back
// to createdAt). Returns null when there are no dated plans.
export const getLatestDietUpdate = (plans: DietPlan[]): Date | null => {
  let latest = 0;
  for (const plan of plans) {
    latest = Math.max(latest, toTime(plan.updatedAt), toTime(plan.createdAt));
  }
  return latest ? new Date(latest) : null;
};

// Most recent add/edit across a client's BMR diet entries.
//
// IMPORTANT: this only looks at `updatedAt`, which is stamped solely when a BMR
// is added or edited from now on. Older BMRs (created before this feature) have
// no `updatedAt`, so they never trigger the review dot until they're edited.
export const getLatestBmrUpdate = (
  diet?: Array<{ updatedAt?: string }> | null
): Date | null => {
  if (!diet || diet.length === 0) return null;
  let latest = 0;
  for (const entry of diet) {
    latest = Math.max(latest, toTime(entry.updatedAt));
  }
  return latest ? new Date(latest) : null;
};

export const daysSince = (date: Date): number =>
  (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);

// A client needs a diet review when their latest diet edit is at least
// DIET_REVIEW_THRESHOLD_DAYS old AND it has not been reviewed since that edit.
export const needsDietReview = (
  latestUpdate: Date | null,
  reviewedAt?: string | Date | null
): boolean => {
  if (!latestUpdate) return false;
  if (daysSince(latestUpdate) < DIET_REVIEW_THRESHOLD_DAYS) return false;
  return toTime(reviewedAt) < latestUpdate.getTime();
};
